/**
 * Image Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 854-2064
 *
 * An image container with optional close button and icon overlay.
 * Supports empty (placeholder) and filled (with source) states.
 */

import { useOnlineStatus } from '@gripp/shared-logic';
import React, { useEffect, useState } from 'react';
import {
  ImageSourcePropType,
  ImageStyle,
  Pressable,
  Image as RNImage,
  StyleProp,
  StyleSheet,
  View,
} from 'react-native';
import { Colors } from '../../../../themes/colors';
import { ImageIcon, XIcon } from '../../icons';
import { colors } from '../../tokens';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

export type ImageState = 'default' | 'filled';

export interface ImageComponentProps {
  /**
   * Image source - when provided, shows "filled" state
   */
  source?: ImageSourcePropType;

  /**
   * Image size (width and height)
   * @default 88
   */
  size?: number;

  /**
   * Border radius
   * @default 4
   */
  borderRadius?: number;

  /**
   * Show close (x) button
   * @default true
   */
  showClose?: boolean;

  /**
   * Show placeholder icon when no image
   * @default true
   */
  showIcon?: boolean;

  /**
   * Callback when close button is pressed
   */
  onClose?: () => void;

  /**
   * Callback when image is pressed
   */
  onPress?: () => void;

  /**
   * Custom image style
   */
  imageStyle?: StyleProp<ImageStyle>;

  /**
   * Test ID for testing
   */
  testID?: string;

  /**
   * Upload progress (0-1)
   */
  progress?: number;

  /**
   * Whether the image is currently uploading
   */
  uploading?: boolean;
}

export const ImageComponent: React.FC<ImageComponentProps> = ({
  source,
  size = 88,
  borderRadius = 4,
  showClose = true,
  showIcon = true,
  onClose,
  onPress,
  imageStyle,
  testID,
  progress,
  uploading,
}) => {
  const [loadFailed, setLoadFailed] = useState(false);
  const sourceKey =
    source && typeof source === 'object' && 'uri' in source
      ? source.uri
      : source;
  const { isOnline } = useOnlineStatus();
  // A new source gets a fresh attempt; a failed one (offline, expired URL)
  // falls back to the empty-state placeholder. Reconnecting also retries:
  // a refetch usually hands back the same URI, which would not reset it.
  useEffect(() => setLoadFailed(false), [sourceKey]);
  useEffect(() => {
    if (isOnline) setLoadFailed(false);
  }, [isOnline]);
  const isFilled = !!source && !loadFailed;

  const containerStyle = {
    width: size,
    height: size,
    borderRadius,
  };

  const handleClose = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    onClose?.();
  };

  const content = (
    <View
      style={[styles.container, containerStyle]}
      testID={testID}
      accessibilityRole="image"
    >
      {isFilled ? (
        <>
          <RNImage
            source={source}
            style={[styles.image, { borderRadius }, imageStyle]}
            resizeMode="cover"
            onError={() => setLoadFailed(true)}
          />
          <View style={[styles.overlay, { borderRadius }]} />
        </>
      ) : (
        <View style={[styles.placeholder, { borderRadius }]}>
          {(showIcon || loadFailed) && (
            <ImageIcon
              size={size * 0.27} // ~24px for 88px container
              color={Colors.role.textSoft}
            />
          )}
        </View>
      )}

      {uploading && (
        <View style={[styles.uploadingOverlay, { borderRadius }]}>
          <View style={styles.progressBarTrack}>
            <View
              style={[
                styles.progressBarFill,
                {
                  width: `${Math.min(
                    Math.max((progress ?? 0) * 100, 0),
                    100
                  )}%`,
                },
              ]}
            />
          </View>
        </View>
      )}

      {showClose && (
        <Pressable
          style={styles.closeButton}
          onPress={handleClose}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Remove image"
        >
          <XIcon size={8} color={Colors.role.textStrong} />
        </Pressable>
      )}
    </View>
  );

  if (onPress) {
    return (
      <Pressable onPress={onPress} accessibilityRole="button">
        {content}
      </Pressable>
    );
  }

  return content;
};

ImageComponent.displayName = 'ImageComponent';

const styles = themedStyleSheet((role) => ({
  container: {
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 2, 2, 0.2)',
  },
  placeholder: {
    flex: 1,
    backgroundColor: role.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 99,
    backgroundColor: role.surfaceInverseSoftHover,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 2, 2, 0.5)',
    justifyContent: 'flex-end',
    padding: 6,
  },
  progressBarTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    overflow: 'hidden',
  },
  // Constant: the upload progress bar is drawn over the image itself.
  progressBarFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: '#FFFFFF',
  },
}));
